import React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';

import { Path, PathsDocument } from '@edx/graphql/generated';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { useAppConfigStateContext } from '@edx/contexts/app-config';
import { useAuth } from '@edx/hooks/use-auth';
import { ResponsiveImage } from '@edx/types/responsive-image';
import { Hero } from '@edx/components/hero';
import { PathEntries } from '@edx/components/path-entries';
import { EnrollBanner } from '@edx/components/banners';

type Props = {
  bgImage: ResponsiveImage;
  paths: Path[];
};

export const getStaticProps: GetStaticProps = async () => {
  const { apolloClient } = await getStaticApolloClient();

  const { data } = await apolloClient.query({ query: PathsDocument });

  return { props: { paths: data.paths } };
};

const PathsPage: React.FC<Props> = ({ bgImage, paths }) => {
  const { projectName } = useAppConfigStateContext();
  const { isLoggedOut } = useAuth();

  const metaTitle = 'Itinerarios';
  const metaDescription =
    '¿No sabes por donde empezar? Aprende a tocar el bajo o mejora tu técnica, con nuestros itinerarios.';

  return (
    <>
      <Head>
        <title key="title">{metaTitle}</title>

        <meta key="description" name="description" content={metaDescription} />

        {/* Open Graph */}
        <meta key="og:title" property="og:title" content={metaTitle} />
        <meta key="og:description" property="og:description" content={metaDescription} />
        <meta key="og:image" property="og:image" content="img/paths_og-image.png" />
      </Head>

      <Hero heading="Itinerarios" preheading={`· ${projectName} ·`} bgImage={bgImage} />

      {paths?.length && (
        <div className="-mt-20 relative max-w-full md:max-w-6xl mx-auto px-4 md:px-10 pb-20">
          <PathEntries
            items={paths.map(({ images, ...path }) => ({ ...path, img: images.index as ResponsiveImage }))}
          />
        </div>
      )}

      {isLoggedOut && <EnrollBanner />}
    </>
  );
};

export default PathsPage;
